import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import {
  collection,
  doc,
  getDoc,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAddPayment } from '../contexts/AddPaymentContext';
import ChannelChat from '../components/ChannelChat';
import OfficeBookingDrawer, {
  OfficeBookingDrawerComputedData,
  OfficeBookingDrawerHandle,
} from '../components/OfficeBookingDrawer';
import { normalizeContactId, normalizePhoneDigits } from '../utils/messaging';

/* ─── types ─────────────────────────────────────────────── */
type UnitBookingStatus = 'available' | 'blocked' | 'booked' | 'sold';

type UnitRow = {
  id: string;
  unitNumber?: string | null;
  unitNumberLower?: string | null;
  blockType?: string | null;
  usage?: string | null;
  unitAmount?: number | null;
  carpetArea?: number | null;
  exclusiveBalconyArea?: number | null;
  exclusiveWashArea?: number | null;
  areaOfExclusiveWash?: number | null;
  area_of_exclusive_wash?: number | null;
  openTerraceArea?: number | null;
  bookingStatus?: UnitBookingStatus | null;
  activeBookingId?: string | null;
  hasBookingFormDate?: boolean | null;
  latestBookingFormDate?: string | null;
  isActive?: boolean | null;
};

/* ─── constants ──────────────────────────────────────────── */
const GOLD       = '#9d6f32';
const GOLD_LIGHT = 'rgba(157,111,50,0.10)';
const BORDER     = '#ebebeb';

const STATUS_META: Record<UnitBookingStatus, { label: string; color: string; bg: string; border: string }> = {
  available: { label: 'Available', color: '#166534', bg: '#dcfce7',               border: '#bbf7d0' },
  blocked:   { label: 'Blocked',   color: '#92400e', bg: '#fef3c7',               border: '#fde68a' },
  booked:    { label: 'Booked',    color: '#9d6f32', bg: 'rgba(157,111,50,0.10)', border: 'rgba(157,111,50,0.28)' },
  sold:      { label: 'Sold',      color: '#7c3aed', bg: '#ede9fe',               border: '#c4b5fd' },
};

const CHIPS = [
  { id: 'overview',     label: 'Overview'     },
  { id: 'pending-work', label: 'Pending Work' },
  { id: 'customers',    label: 'Customers'    },
  { id: 'change-orders', label: 'Change Orders' },
  { id: 'payments',     label: 'Payments & GST' },
  { id: 'payment-roadmap', label: 'Plan & Collection' },
  { id: 'documents',    label: 'Documents & Loan' },
] as const;
type ChipId = typeof CHIPS[number]['id'];

/** Always shows exact Indian number format — used in sidebar where full digits match the mockup */
function fmtInrExact(n: number | null | undefined): string {
  if (n == null || n === 0) return '—';
  return `₹${new Intl.NumberFormat('en-IN').format(Math.round(n))}`;
}

const SECTION_HEADER_SX = {
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: 0.9,
  color: '#a0a0a0',
  textTransform: 'uppercase' as const,
  mb: 1.25,
};

/* ─── page ───────────────────────────────────────────────── */
export default function OfficeUnitDetailClassicPage() {
  const { unitId }           = useParams<{ unitId: string }>();
  const navigate             = useNavigate();
  const location             = useLocation();
  const fromOfficeToday      = location.state?.fromOfficeToday === true;
  const backPath             = fromOfficeToday ? '/office/today' : '/office/units';
  const { currentProjectId } = useAuth();
  const db                   = getFirestore();
  const theme                = useTheme();
  const isMobile             = useMediaQuery(theme.breakpoints.down('sm'));

  const drawerRef = useRef<OfficeBookingDrawerHandle>(null);
  const { openAddPayment } = useAddPayment();

  const [allUnits, setAllUnits]         = useState<UnitRow[]>([]);
  const [unitsLoaded, setUnitsLoaded]   = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [projectSbPercent, setProjectSbPercent]               = useState(0);
  const [projectSbPercentByType, setProjectSbPercentByType]   = useState<Record<string, number>>({});
  const [activeChip, setActiveChip]     = useState<ChipId>('overview');
  const [bfAnchorEl, setBfAnchorEl]     = useState<HTMLElement | null>(null);
  const [whatsappExpanded, setWhatsappExpanded] = useState(false);
  const [mobileSummaryExpanded, setMobileSummaryExpanded] = useState(false);
  const [mobileSummaryHeight, setMobileSummaryHeight] = useState(360);
  const [desktopSidebarWidth, setDesktopSidebarWidth] = useState(353);
  const [legacyDrawerOpen, setLegacyDrawerOpen] = useState(false);
  const bfHoverTimer                    = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [computedData, setComputedData] = useState<OfficeBookingDrawerComputedData>({
    customerName: null,
    bookingId: null,
    customerReceiptTotal: 0,
    loanDisbursementReceiptTotal: 0,
    loanToDeveloperAmount: 0,
    gstReceiptTotal: 0,
    gstDueAmount: 0,
    stampDutyReceiptTotal: 0,
    registrationReceiptTotal: 0,
    hasDraftDocuments: false,
    bookingFormHoverDetails: null,
    bookingFormUrl: null,
    bookingFormFileName: null,
    saleAgreementDate: null,
    saleDeedDate: null,
    customers: [],
    sectionAvailability: {
      customers: false,
      changeOrders: false,
      payments: false,
      paymentRoadmap: false,
      documentsLoan: false,
    },
  });

  const startMobileSummaryResize = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isMobile || !mobileSummaryExpanded) return;
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = mobileSummaryHeight;
    const minimumHeight = 190;
    const maximumHeight = Math.min(window.innerHeight * 0.72, 640);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const nextHeight = Math.max(
        minimumHeight,
        Math.min(maximumHeight, startHeight + moveEvent.clientY - startY),
      );
      setMobileSummaryHeight(Math.round(nextHeight));
    };
    const stopResizing = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResizing);
      window.removeEventListener('pointercancel', stopResizing);
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResizing);
    window.addEventListener('pointercancel', stopResizing);
  }, [isMobile, mobileSummaryExpanded, mobileSummaryHeight]);

  const startDesktopSidebarResize = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (isMobile) return;
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = desktopSidebarWidth;
    const minimumWidth = 270;
    const maximumWidth = Math.min(window.innerWidth * 0.48, 560);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const nextWidth = Math.max(
        minimumWidth,
        Math.min(maximumWidth, startWidth + moveEvent.clientX - startX),
      );
      setDesktopSidebarWidth(Math.round(nextWidth));
    };
    const stopResizing = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResizing);
      window.removeEventListener('pointercancel', stopResizing);
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResizing);
    window.addEventListener('pointercancel', stopResizing);
  }, [desktopSidebarWidth, isMobile]);

  /* load units */
  useEffect(() => {
    if (!currentProjectId) return;
    const q = query(
      collection(db, 'projects', currentProjectId, 'units'),
      orderBy('unitNumberLower', 'asc'),
      limit(5000)
    );
    const unsub = onSnapshot(q, snap => {
      setAllUnits(snap.docs.map(d => ({ id: d.id, ...d.data() } as UnitRow)));
      setUnitsLoaded(true);
    });
    return unsub;
  }, [currentProjectId, db]);

  /* load project SB percent */
  useEffect(() => {
    if (!currentProjectId) return;
    getDoc(doc(db, 'projects', currentProjectId)).then(snap => {
      if (!snap.exists()) return;
      const d = snap.data() as any;
      setProjectSbPercent(Math.max(0, Number(d.sbAreaPercent || 0) || 0));
      const raw: Record<string, any> = {
        ...(d.sbAreaPercentByUnitType || {}),
        ...(d.sbAreaPercentByUsage || {}),
      };
      const parsed: Record<string, number> = {};
      Object.keys(raw).forEach(k => {
        const v = Number(raw[k]);
        if (Number.isFinite(v) && v >= 0) parsed[k.trim()] = v;
      });
      setProjectSbPercentByType(parsed);
    }).catch(() => {});
  }, [currentProjectId, db]);

  const unit   = useMemo(() => allUnits.find(u => u.id === unitId) ?? null, [allUnits, unitId]);
  const status = (unit?.bookingStatus || 'available') as UnitBookingStatus;
  const meta   = STATUS_META[status];

  /* live data from drawer */
  const handleComputedData = useCallback((data: OfficeBookingDrawerComputedData) => {
    setComputedData(data);
  }, []);

  /* chip filter navigation */
  const handleChipClick = useCallback((chipId: ChipId) => {
    setActiveChip(chipId);
  }, []);

  const visibleChips = useMemo(() => CHIPS.filter((chip) => {
    if (chip.id === 'overview') return true;
    if (chip.id === 'pending-work') return status === 'booked' || status === 'sold';
    if (chip.id === 'customers') return computedData.sectionAvailability.customers;
    if (chip.id === 'change-orders') return computedData.sectionAvailability.changeOrders;
    if (chip.id === 'payments') return computedData.sectionAvailability.payments;
    if (chip.id === 'payment-roadmap') return computedData.sectionAvailability.paymentRoadmap;
    if (chip.id === 'documents') return computedData.sectionAvailability.documentsLoan;
    return false;
  }), [computedData.sectionAvailability, status]);

  useEffect(() => {
    if (!visibleChips.some((chip) => chip.id === activeChip)) {
      setActiveChip('overview');
    }
  }, [activeChip, visibleChips]);

  /* ── Loading ── */
  if (!unitsLoaded) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={24} sx={{ color: GOLD }} />
      </Box>
    );
  }

  /* ── Not found ── */
  if (!unit) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5 }}>
        <HomeWorkOutlinedIcon sx={{ fontSize: 40, color: '#d0d0d0' }} />
        <Typography sx={{ fontSize: 14, color: '#6b6b6b' }}>Unit not found.</Typography>
        <Box
          component="span"
          onClick={() => navigate(backPath)}
          sx={{ fontSize: 13, color: GOLD, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}
        >
          ← Back to {fromOfficeToday ? 'Today' : 'units'}
        </Box>
      </Box>
    );
  }

  /* ── Computed sidebar values ── */
  const unitAmount = typeof unit.unitAmount === 'number' ? unit.unitAmount : 0;
  const loanToDeveloperAmount = Math.max(0, Number(computedData.loanToDeveloperAmount || 0) || 0);
  const totalReceivedAmount = Math.max(
    0,
    Number(computedData.customerReceiptTotal || 0) + Number(computedData.loanDisbursementReceiptTotal || 0)
  );
  const paidPercent = unitAmount > 0
    ? Math.min(100, (totalReceivedAmount / unitAmount) * 100)
    : 0;
  const pendingAmount = Math.max(0, unitAmount - totalReceivedAmount);

  const ceilToHundred = (v: number) => Math.ceil(v / 100) * 100;
  const stampDutyAmount         = unitAmount > 0 ? ceilToHundred(ceilToHundred(unitAmount) * 0.049) : 0;
  const hasMaleCustomer = computedData.customers.some(
    (customer) => String((customer as any)?.gender || '').trim().toLowerCase() === 'male'
  );
  const registrationChargesAmount =
    unitAmount <= 0
      ? 0
      : computedData.customers.length === 0 || hasMaleCustomer
      ? ceilToHundred(ceilToHundred(unitAmount) * 0.01) + 1200
      : 1200;

  const washArea =
    typeof unit.exclusiveWashArea === 'number' ? unit.exclusiveWashArea
    : typeof unit.areaOfExclusiveWash === 'number' ? unit.areaOfExclusiveWash
    : typeof unit.area_of_exclusive_wash === 'number' ? unit.area_of_exclusive_wash
    : 0;
  const totalAreaSqmt =
    (unit.carpetArea || 0) +
    (unit.exclusiveBalconyArea || 0) +
    (washArea || 0) +
    (unit.openTerraceArea || 0);
  const totalAreaSqftRaw = totalAreaSqmt * 10.764;
  const totalAreaSqft    = Math.ceil(totalAreaSqftRaw);

  // SB (Super Built-up) derived area
  const unitTypeKey = [unit.usage, (unit as any).blockType, (unit as any).unitType, (unit as any).type]
    .map(v => String(v || '').trim()).find(v => v) || '';
  const sbPercent = unitTypeKey && projectSbPercentByType[unitTypeKey] != null
    ? projectSbPercentByType[unitTypeKey]
    : projectSbPercent;
  const sbDerivedSqft = sbPercent > 0 && sbPercent < 100
    ? Math.ceil(totalAreaSqftRaw / (1 - sbPercent / 100))
    : null;
  const primaryCustomerForWhatsApp = computedData.customers.find((customer) =>
    String(customer.role || '').toLowerCase().includes('primary')
  ) || computedData.customers[0] || null;
  const primaryWhatsAppDigits = normalizePhoneDigits(String(primaryCustomerForWhatsApp?.mobile || ''));
  const primaryWhatsAppContactId = primaryWhatsAppDigits
    ? normalizeContactId('whatsapp', primaryWhatsAppDigits)
    : '';

  /* ─── helpers ─── */
  const finRow = (label: string, amount: number, paid?: boolean) => (
    <Stack key={label} direction="row" justifyContent="space-between" alignItems="center">
      <Typography sx={{ fontSize: 12.5, color: '#5a5a5a' }}>{label}</Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {paid && <CheckCircleIcon sx={{ fontSize: 14, color: '#2a7d4b' }} />}
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#1a1a1a' }}>{fmtInrExact(amount)}</Typography>
      </Stack>
    </Stack>
  );

  const areaRow = (label: string, value: number | null | undefined, icon: React.ReactNode) => (
    <Stack key={label} direction="row" alignItems="center" spacing={0.875}>
      <Box sx={{ color: '#5a5a5a', display: 'flex', alignItems: 'center', fontSize: 15, flexShrink: 0 }}>{icon}</Box>
      <Typography sx={{ fontSize: 12.5, color: '#5a5a5a', flex: 1 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#1a1a1a' }}>
        {value != null ? `${value} sq mt` : '—'}
      </Typography>
    </Stack>
  );

  /* ── Page ── */
  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden', bgcolor: '#f4f5f7' }}>

      {/* ─────── Header ─────── */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: '#ffffff',
          borderBottom: `1px solid ${BORDER}`,
          borderTop: `3px solid ${GOLD}`,
          px: { xs: 1.25, sm: 3 },
          py: 0,
        }}
      >
        {/* Breadcrumb */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pt: { xs: 0.75, sm: 1.25 }, pb: { xs: 0.25, sm: 0.5 } }}>
          <Box
            onClick={() => navigate(backPath)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.4,
              color: '#9b9b9b', fontSize: 12, cursor: 'pointer', userSelect: 'none',
              transition: 'color 0.15s', '&:hover': { color: GOLD },
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 13 }} />
            {fromOfficeToday ? 'Today' : 'Units'}
          </Box>
          <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 12, color: '#d8d8d8' }}>/</Typography>
          <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 12, color: '#9b9b9b' }}>{unit.unitNumber || unitId}</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/office/units/${unit.id}`, { state: location.state })}
            sx={{ ml: 'auto !important', minWidth: 0, px: 1.25, py: 0.25, borderColor: GOLD, color: GOLD, textTransform: 'none', fontWeight: 700, '&:hover': { borderColor: GOLD, bgcolor: GOLD_LIGHT } }}
          >
            New view
          </Button>
        </Stack>

        {/* Title row */}
        <Stack
          direction={{ xs: 'row', sm: 'row' }}
          alignItems="center"
          sx={{
            pb: { xs: 1, sm: 1.5 },
            gap: { xs: 0.75, sm: 2 },
            display: { xs: 'grid', sm: 'flex' },
            gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', sm: 'none' },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.1}
            flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
            rowGap={0.6}
            sx={{ minWidth: 0, flex: 1, gridColumn: { xs: '1 / -1', sm: 'auto' }, width: '100%' }}
          >
            <Typography
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontWeight: 900, fontSize: 26, color: '#1a1a1a',
                lineHeight: 1.1, letterSpacing: '-0.5px',
              }}
            >
              {unit.unitNumber || unit.id}
            </Typography>

            {/* Status badge */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center',
              px: 1, py: 0.3, borderRadius: '6px',
              bgcolor: meta.bg, border: `1px solid ${meta.border}`,
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: meta.color, mr: 0.75, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: meta.color, lineHeight: 1 }}>
                {meta.label}
              </Typography>
            </Box>

            {/* Block type pill */}
            {unit.blockType && (
              <Typography sx={{
                fontSize: 11, color: '#7b7b7b', bgcolor: '#f4f4f4',
                border: '1px solid #e8e8e8', borderRadius: '5px',
                px: 0.875, py: 0.25, lineHeight: 1.4, fontWeight: 500,
              }}>
                {unit.blockType}
              </Typography>
            )}

            <Tooltip title="Open drawer view" placement="bottom" arrow>
              <IconButton
                aria-label="Open drawer view"
                size="small"
                onClick={() => setLegacyDrawerOpen(true)}
                sx={{
                  display: { xs: 'inline-flex', sm: 'none' },
                  ml: 'auto !important',
                  width: 34,
                  height: 34,
                  borderRadius: '9px',
                  border: '1px solid #d9d9d9',
                  bgcolor: '#f7f7f7',
                  color: '#5a5a5a',
                }}
              >
                <ViewSidebarOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Action icon buttons */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mr: { xs: 0, sm: 1 }, flexShrink: 0, gridColumn: { xs: 1, sm: 'auto' } }}>

            {/* Add payment — opens new unified AddPaymentDrawer */}
            <Tooltip title="Add payment" placement="bottom" arrow>
              <IconButton
                size="small"
                onClick={() =>
                  openAddPayment({
                    unitId:          unitId ?? undefined,
                    unitNumber:      unit?.unitNumber ?? undefined,
                    bookingId:       computedData.bookingId ?? undefined,
                    partyName:       computedData.customerName ?? undefined,
                    receiptCategory: 'customer',
                  })
                }
                sx={{
                  width: 34, height: 34,
                  bgcolor: '#f5f5f5',
                  border: '1px solid #e8e8e8',
                  borderRadius: '9px',
                  color: '#5a5a5a',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: GOLD_LIGHT, borderColor: GOLD, color: GOLD },
                  '& svg': { fontSize: 17 },
                }}
              >
                <CurrencyRupeeOutlinedIcon />
              </IconButton>
            </Tooltip>

            {/* Booking form icon — click opens dialog, hover shows details popover */}
            <Tooltip
              title={computedData.bookingFormHoverDetails ? '' : 'Booking form'}
              placement="bottom"
              arrow
            >
              <IconButton
                size="small"
                onClick={() => drawerRef.current?.triggerBookingForm()}
                onMouseEnter={(e) => {
                  if (!computedData.bookingFormHoverDetails) return;
                  const target = e.currentTarget;
                  bfHoverTimer.current = setTimeout(() => setBfAnchorEl(target), 400);
                }}
                onMouseLeave={() => {
                  if (bfHoverTimer.current) clearTimeout(bfHoverTimer.current);
                  setBfAnchorEl(null);
                }}
                sx={{
                  width: 34, height: 34,
                  bgcolor: '#f5f5f5',
                  border: '1px solid #e8e8e8',
                  borderRadius: '9px',
                  color: '#5a5a5a',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: GOLD_LIGHT, borderColor: GOLD, color: GOLD },
                  '& svg': { fontSize: 17 },
                }}
              >
                <DescriptionOutlinedIcon />
              </IconButton>
            </Tooltip>

            {/* Download booking form — only when a form file exists */}
            {computedData.bookingFormUrl && (
              <Tooltip title="Download Booking Form" placement="bottom" arrow>
                <IconButton
                  size="small"
                  onClick={() => drawerRef.current?.triggerDownloadBookingForm()}
                  sx={{
                    width: 34, height: 34,
                    bgcolor: '#f5f5f5',
                    border: '1px solid #e8e8e8',
                    borderRadius: '9px',
                    color: '#5a5a5a',
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: GOLD_LIGHT, borderColor: GOLD, color: GOLD },
                    '& svg': { fontSize: 17 },
                  }}
                >
                  <DownloadOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Settings icon — opens full admin dropdown */}
            <Tooltip title="Admin actions" placement="bottom" arrow>
              <IconButton
                size="small"
                onClick={(e) => drawerRef.current?.triggerAdminMenu(e.currentTarget)}
                sx={{
                  width: 34, height: 34,
                  bgcolor: '#f5f5f5',
                  border: '1px solid #e8e8e8',
                  borderRadius: '9px',
                  color: '#5a5a5a',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: GOLD_LIGHT, borderColor: GOLD, color: GOLD },
                  '& svg': { fontSize: 17 },
                }}
              >
                <SettingsOutlinedIcon />
              </IconButton>
            </Tooltip>

          </Stack>

          {/* Booking form hover details popover */}
          <Popover
            open={Boolean(bfAnchorEl)}
            anchorEl={bfAnchorEl}
            onClose={() => setBfAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            disableRestoreFocus
            sx={{ pointerEvents: 'none', mt: 0.5 }}
            slotProps={{ paper: { sx: { borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', p: 1.75, minWidth: 220 } } }}
          >
            {computedData.bookingFormHoverDetails && (
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: '#1a1a1a', mb: 0.75 }}>
                  Booking Form Details
                </Typography>
                {[
                  { label: 'File',      value: computedData.bookingFormHoverDetails.fileName },
                  { label: 'Date/Time', value: computedData.bookingFormHoverDetails.enteredDateTime || computedData.bookingFormHoverDetails.generatedAt },
                  { label: 'Ref By',   value: computedData.bookingFormHoverDetails.refBy },
                  { label: 'Rank',     value: computedData.bookingFormHoverDetails.rank },
                  { label: 'Scheme',   value: computedData.bookingFormHoverDetails.scheme },
                  { label: 'Remark',   value: computedData.bookingFormHoverDetails.schemeRemark },
                ].filter(r => r.value).map(r => (
                  <Stack key={r.label} direction="row" spacing={0.75} sx={{ mb: 0.3 }}>
                    <Typography sx={{ fontSize: 11, color: '#9b9b9b', minWidth: 60, flexShrink: 0 }}>{r.label}:</Typography>
                    <Typography sx={{ fontSize: 11, color: '#1a1a1a', fontWeight: 500, wordBreak: 'break-word' }}>{r.value}</Typography>
                  </Stack>
                ))}
              </Box>
            )}
          </Popover>

          {/* Unit switcher */}
          <Box sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' }, minWidth: 0, gridColumn: { xs: 2, sm: 'auto' }, display: 'flex', alignItems: 'center' }}>
            <Autocomplete<UnitRow, false, true, false>
              open={switcherOpen}
              onOpen={() => setSwitcherOpen(true)}
              onClose={() => setSwitcherOpen(false)}
              value={unit}
              options={allUnits}
              getOptionLabel={u => u.unitNumber || u.id}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, next) => { if (next) navigate(`/office/units/${next.id}`, { state: location.state }); }}
              size="small"
              disableClearable
              blurOnSelect
              sx={{ width: { xs: '100%', sm: 220 } }}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Switch unit…"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <HomeWorkOutlinedIcon sx={{ fontSize: 15, color: '#b0b0b0', mr: 0.5, ml: 0.25 }} />
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-input': { fontSize: 12, color: '#3a3a3a', py: '5px !important' },
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#fafafa',
                      '& fieldset': { borderColor: BORDER },
                      '&:hover fieldset': { borderColor: GOLD },
                      '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: 1 },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => {
                const s = (option.bookingStatus || 'available') as UnitBookingStatus;
                const m = STATUS_META[s];
                const isCurrent = option.id === unitId;
                return (
                  <Box
                    component="li"
                    {...props}
                    key={option.id}
                    sx={{
                      py: '5px !important', px: '10px !important',
                      bgcolor: isCurrent ? GOLD_LIGHT : 'transparent',
                      '&:hover': { bgcolor: isCurrent ? GOLD_LIGHT : '#f5f5f5' },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      <Typography sx={{ fontSize: 13, fontWeight: isCurrent ? 700 : 500, flex: 1, minWidth: 0 }} noWrap>
                        {option.unitNumber || option.id}
                      </Typography>
                      {option.blockType && (
                        <Typography sx={{ fontSize: 10, color: '#9b9b9b', flexShrink: 0 }}>
                          {option.blockType}
                        </Typography>
                      )}
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: m.color, flexShrink: 0, opacity: 0.8 }} />
                    </Stack>
                  </Box>
                );
              }}
            />
          </Box>
        </Stack>
      </Box>

      {/* ─────── Two-column body ─────── */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, overflow: 'hidden' }}>

        {/* ── Left sidebar ── */}
        <Box
          sx={{
            width: { xs: '100%', sm: desktopSidebarWidth },
            flexShrink: 0,
            borderRight: 'none',
            borderBottom: { xs: `1px solid ${BORDER}`, sm: 'none' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            height: { xs: mobileSummaryExpanded ? `${mobileSummaryHeight}px` : 66, sm: 'auto' },
            maxHeight: { xs: mobileSummaryExpanded ? '72vh' : 66, sm: 'none' },
            bgcolor: { xs: mobileSummaryExpanded ? '#ffffff' : '#f8f5ef', sm: '#ffffff' },
          }}
        >
          {isMobile && (
            <Box
              component="button"
              type="button"
              onClick={() => setMobileSummaryExpanded((expanded) => !expanded)}
              aria-expanded={mobileSummaryExpanded}
              sx={{
                width: '100%',
                minHeight: 65,
                px: 1.5,
                py: 1,
                border: 0,
                bgcolor: mobileSummaryExpanded ? '#ffffff' : '#f8f5ef',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#8b8b8b', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  Unit summary
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: 13, fontWeight: 700, color: '#1a1a1a' }} noWrap>
                  {computedData.customerName || 'No customer'} · {unitAmount > 0 ? fmtInrExact(pendingAmount) : '—'} pending
                </Typography>
              </Box>
              <ExpandMoreIcon
                sx={{
                  flexShrink: 0,
                  color: '#777',
                  transform: mobileSummaryExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.18s ease',
                }}
              />
            </Box>
          )}

          {/* Scrollable sidebar content */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, pt: { xs: 1.5, sm: 2.5 }, pb: 1, display: { xs: mobileSummaryExpanded ? 'block' : 'none', sm: 'block' } }}>

            {/* ── CUSTOMER ── */}
            <Box sx={{ mb: 2.25 }}>
              <Typography sx={SECTION_HEADER_SX}>Customer</Typography>
              {computedData.customers.length > 0 ? (
                <Stack spacing={1.5}>
                  {computedData.customers.map((c, i) => (
                    <Stack key={i} direction="row" alignItems="center" spacing={1.25}>
                      {/* Avatar */}
                      <Box
                        sx={{
                          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                          bgcolor: i === 0 ? '#f4ece0' : '#ebebf0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Typography sx={{ fontSize: 11, fontWeight: 800, color: i === 0 ? '#8a5c1a' : '#6b6b90', lineHeight: 1 }}>
                          {c.initials}
                        </Typography>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }} noWrap>
                          {c.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#9b9b9b', mt: 0.2 }}>
                          {c.role}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography sx={{ fontSize: 13, color: '#c0c0c0', fontStyle: 'italic' }}>
                  No customer yet
                </Typography>
              )}
            </Box>

            <Divider sx={{ mx: -2.5, mb: 2.25 }} />

            {/* ── FINANCIALS ── */}
            <Box sx={{ mb: 2.25 }}>
              <Typography sx={SECTION_HEADER_SX}>Financials</Typography>

              {/* Unit amount */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                <Typography sx={{ fontSize: 12.5, color: '#5a5a5a' }}>Unit amount</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {unitAmount > 0 && totalReceivedAmount >= unitAmount && (
                    <CheckCircleIcon sx={{ fontSize: 14, color: '#2a7d4b' }} />
                  )}
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>
                    {unitAmount > 0 ? fmtInrExact(unitAmount) : '—'}
                  </Typography>
                </Stack>
              </Stack>

              {/* Received + pending — only show when something has been received */}
              {unitAmount > 0 && (totalReceivedAmount > 0 || loanToDeveloperAmount > 0 || pendingAmount > 0) && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.25} sx={{ mb: 0.85 }}>
                  {totalReceivedAmount > 0 && (
                    <Typography sx={{ fontSize: 11.5, color: '#2a7d4b', fontWeight: 600 }}>
                      ✓ {fmtInrExact(totalReceivedAmount)} received
                    </Typography>
                  )}
                  {loanToDeveloperAmount > 0 && (
                    <Typography sx={{ fontSize: 11.5, color: 'primary.main', fontWeight: 600 }}>
                      {fmtInrExact(loanToDeveloperAmount)} loan
                    </Typography>
                  )}
                  {pendingAmount > 0 && (
                    <Typography sx={{ fontSize: 11.5, color: '#c85a2a', fontWeight: 600 }}>
                      {fmtInrExact(pendingAmount)} pending
                    </Typography>
                  )}
                </Stack>
              )}

              {/* Progress bar */}
              {unitAmount > 0 && (
                <LinearProgress
                  variant="determinate"
                  value={paidPercent}
                  sx={{
                    height: 7, borderRadius: 4, mb: 1.75,
                    bgcolor: 'rgba(157,111,50,0.12)',
                    '& .MuiLinearProgress-bar': { bgcolor: '#9d6f32', borderRadius: 4 },
                  }}
                />
              )}

              {/* GST / Stamp / Reg rows */}
              <Stack spacing={0.7}>
                {computedData.gstDueAmount > 0 && finRow(
                  'GST', computedData.gstDueAmount,
                  computedData.gstReceiptTotal >= computedData.gstDueAmount
                )}
                {stampDutyAmount > 0 && finRow(
                  'Stamp duty', stampDutyAmount,
                  computedData.stampDutyReceiptTotal >= stampDutyAmount
                )}
                {registrationChargesAmount > 0 && finRow(
                  'Reg. charges', registrationChargesAmount,
                  computedData.registrationReceiptTotal >= registrationChargesAmount
                )}
              </Stack>
            </Box>

            {/* ── AREAS ── */}
            {(unit.carpetArea != null || unit.exclusiveBalconyArea != null || washArea > 0 || unit.openTerraceArea != null) && (
              <>
                <Divider sx={{ mx: -2.5, mb: 2.25 }} />
                <Box sx={{ mb: 2.25 }}>
                  <Typography sx={SECTION_HEADER_SX}>Areas</Typography>
                  <Stack spacing={0.8}>
                    {unit.carpetArea != null &&
                      areaRow('Carpet', unit.carpetArea, <StraightenOutlinedIcon fontSize="inherit" />)
                    }
                    {unit.exclusiveBalconyArea != null &&
                      areaRow('Excl. Balcony', unit.exclusiveBalconyArea, <HomeOutlinedIcon fontSize="inherit" />)
                    }
                    {washArea > 0 &&
                      areaRow('Excl. Wash', washArea, <HomeOutlinedIcon fontSize="inherit" />)
                    }
                    {unit.openTerraceArea != null &&
                      areaRow('Open Terrace', unit.openTerraceArea, <HomeOutlinedIcon fontSize="inherit" />)
                    }
                    {totalAreaSqmt > 0 && (
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ pt: 0.5 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1a1a1a' }}>Total</Typography>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1a1a1a' }}>
                          {totalAreaSqft} sq ft
                          {sbDerivedSqft != null && (
                            <Typography component="span" sx={{ fontSize: 11, fontWeight: 500, color: '#7a7a7a', ml: 0.5 }}>
                              ({sbDerivedSqft} sq ft)
                            </Typography>
                          )}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              </>
            )}

            {primaryWhatsAppContactId && (
              <>
                <Divider sx={{ mx: -2.5, mb: 2.25 }} />
                <Box sx={{ mb: 2.25 }}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setWhatsappExpanded((value) => !value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setWhatsappExpanded((value) => !value);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      cursor: 'pointer',
                      userSelect: 'none',
                      mb: whatsappExpanded ? 1 : 0,
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                      <WhatsAppIcon sx={{ color: '#25D366', fontSize: 18, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={SECTION_HEADER_SX}>WhatsApp Messages</Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#7a7a7a', mt: -0.75 }} noWrap>
                          {primaryCustomerForWhatsApp?.name || computedData.customerName || 'Primary customer'}
                        </Typography>
                      </Box>
                    </Stack>
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 18,
                        color: '#7a7a7a',
                        transform: whatsappExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.18s ease',
                      }}
                    />
                  </Box>
                  <Collapse in={whatsappExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ border: '1px solid #f0f0f0', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
                      <ChannelChat
                        channel="whatsapp"
                        contactId={primaryWhatsAppContactId}
                        contactName={primaryCustomerForWhatsApp?.name || computedData.customerName || null}
                        customerUnitId={unit.id || ''}
                        customerUnitNumber={unit.unitNumber || unit.id || ''}
                        customerProjectName=""
                        customerName={primaryCustomerForWhatsApp?.name || computedData.customerName || ''}
                        projectId={currentProjectId || undefined}
                        height={360}
                        showHeader={false}
                        canOpenCustomerUnit={false}
                      />
                    </Box>
                  </Collapse>
                </Box>
              </>
            )}

            {/* ── SALE AGREEMENT ── */}
            {computedData.saleAgreementDate && (
              <>
                <Divider sx={{ mx: -2.5, mb: 2.25 }} />
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={SECTION_HEADER_SX}>Sale Agreement</Typography>
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3 }}>
                    {computedData.saleAgreementDate}
                  </Typography>
                </Box>
              </>
            )}

            {/* ── SALE DEED ── */}
            {computedData.saleDeedDate && (
              <>
                <Divider sx={{ mx: -2.5, mb: 2.25 }} />
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={SECTION_HEADER_SX}>Sale Deed</Typography>
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3 }}>
                    {computedData.saleDeedDate}
                  </Typography>
                </Box>
              </>
            )}

          </Box>

          {isMobile && mobileSummaryExpanded ? (
            <Box
              role="separator"
              aria-label="Resize unit summary"
              aria-orientation="horizontal"
              onPointerDown={startMobileSummaryResize}
              sx={{
                height: 16,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'ns-resize',
                touchAction: 'none',
                bgcolor: '#ffffff',
                borderTop: `1px solid ${BORDER}`,
                '&::after': {
                  content: '""',
                  width: 38,
                  height: 3,
                  borderRadius: 2,
                  bgcolor: '#c9c2b8',
                },
              }}
            />
          ) : null}

          {/* Action buttons — pinned at bottom */}
          <Box
            sx={{
              flexShrink: 0,
              px: 1.5,
              py: 1,
              borderTop: `1px solid ${BORDER}`,
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            {(
              [
                { label: 'Add payment',     icon: <CurrencyRupeeOutlinedIcon />, onClick: () => drawerRef.current?.triggerAddPayment(), hover: { bgcolor: GOLD_LIGHT, borderColor: GOLD, color: GOLD } },
                { label: 'Booking form',    icon: <DescriptionOutlinedIcon />,   onClick: () => drawerRef.current?.triggerBookingForm(), hover: { bgcolor: GOLD_LIGHT, borderColor: GOLD, color: GOLD } },
                { label: 'Edit unit status',icon: <SettingsOutlinedIcon />,      onClick: () => drawerRef.current?.triggerEditStatus(), hover: { bgcolor: '#f5f5f5', borderColor: '#b8b8b8', color: '#1a1a1a' } },
              ] as const
            ).map(btn => (
              <Tooltip key={btn.label} title={btn.label} placement="top" arrow>
                <IconButton
                  aria-label={btn.label}
                  onClick={btn.onClick}
                  sx={{
                    width: 40,
                    height: 40,
                    border: '1px solid #d0d0d0',
                    color: '#4a4a4a',
                    bgcolor: '#ffffff',
                    borderRadius: '10px',
                    transition: 'all 0.15s',
                    '&:hover': btn.hover,
                    '& svg': { fontSize: 19 },
                  }}
                >
                  {btn.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </Box>

        <Box
          role="separator"
          aria-label="Resize unit details sidebar"
          aria-orientation="vertical"
          onPointerDown={startDesktopSidebarResize}
          sx={{
            display: { xs: 'none', sm: 'flex' },
            width: 8,
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'ew-resize',
            touchAction: 'none',
            bgcolor: '#ffffff',
            borderRight: `1px solid ${BORDER}`,
            '&::after': {
              content: '""',
              width: 2,
              height: 42,
              borderRadius: 2,
              bgcolor: '#d1cbc2',
              transition: 'background-color 0.15s, height 0.15s',
            },
            '&:hover::after': {
              height: 58,
              bgcolor: GOLD,
            },
          }}
        />

        {/* ── Right panel ── */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Filter chips */}
          <Box
            sx={{
              flexShrink: 0,
              bgcolor: '#ffffff',
              borderBottom: `1px solid ${BORDER}`,
              px: { xs: 1.25, sm: 2.5 },
              py: 1.25,
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            <Stack direction="row" spacing={0.75} sx={{ width: 'max-content' }}>
              {visibleChips.map(chip => {
                const isActive = activeChip === chip.id;
                return (
                  <Box
                    key={chip.id}
                    component="button"
                    onClick={() => handleChipClick(chip.id as ChipId)}
                    sx={{
                      cursor: 'pointer',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '20px',
                      fontSize: 12,
                      fontWeight: isActive ? 700 : 500,
                      lineHeight: 1.4,
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s',
                      bgcolor: isActive ? GOLD_LIGHT : 'transparent',
                      border: `1.5px solid ${isActive ? GOLD : BORDER}`,
                      color: isActive ? GOLD : '#666',
                      '&:hover': {
                        borderColor: GOLD,
                        color: GOLD,
                        bgcolor: GOLD_LIGHT,
                      },
                    }}
                  >
                    {chip.label}
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {/* Drawer content */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
            <OfficeBookingDrawer
              ref={drawerRef}
              open
              onClose={() => navigate(backPath)}
              projectId={currentProjectId ?? ''}
              unit={unit}
              pageMode
              pageModeActiveSection={activeChip}
              onComputedData={handleComputedData}
            />
          </Box>
        </Box>

      </Box>
    </Box>

    {legacyDrawerOpen ? (
      <OfficeBookingDrawer
        open
        onClose={() => setLegacyDrawerOpen(false)}
        projectId={currentProjectId ?? ''}
        unit={unit}
      />
    ) : null}
    </>
  );
}
